import { useEffect, useState } from 'react';
import DateRangePicker from '@/components/base/DateRangePicker';
import SearchableSelect from '@/components/base/SearchableSelect';

/** 上个月自然月起止日期 YYYY-MM-DD */
function lastMonthRange(from = new Date()) {
  const d = new Date(from.getFullYear(), from.getMonth() - 1, 1);
  const y = d.getFullYear();
  const m = d.getMonth();
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    begin: `${y}-${pad(m + 1)}-01`,
    end: `${y}-${pad(m + 1)}-${pad(new Date(y, m + 1, 0).getDate())}`,
  };
}

export interface DownloadTemplateValues {
  departmentTitle: string;
  startTime: string;
  endTime: string;
}

interface DownloadTemplateModalProps {
  isOpen: boolean;
  downloading: boolean;
  showDept: boolean;
  deptOptions: string[];
  onCancel: () => void;
  onConfirm: (values: DownloadTemplateValues) => void;
}

export default function DownloadTemplateModal({
  isOpen,
  downloading,
  showDept,
  deptOptions,
  onCancel,
  onConfirm,
}: DownloadTemplateModalProps) {
  const [deptTitle, setDeptTitle] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [deptError, setDeptError] = useState('');
  const [dateError, setDateError] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    const last = lastMonthRange();
    setDateFrom(last.begin);
    setDateTo(last.end);
    setDeptTitle(deptOptions.length === 1 ? deptOptions[0] : '');
    setDeptError('');
    setDateError('');
  }, [isOpen, deptOptions]);

  if (!isOpen) return null;

  const handleConfirm = () => {
    if (downloading) return;

    if (showDept && !deptTitle) {
      setDeptError('请选择科室');
      return;
    }

    const hasFrom = !!dateFrom;
    const hasTo = !!dateTo;
    if (hasFrom !== hasTo) {
      setDateError('请同时选择起始日期和结束日期');
      return;
    }
    if (hasFrom && hasTo && dateFrom > dateTo) {
      setDateError('结束日期不能早于起始日期');
      return;
    }

    setDeptError('');
    setDateError('');
    onConfirm({
      departmentTitle: deptTitle,
      startTime: dateFrom,
      endTime: dateTo,
    });
  };

  return (
    <div className="fixed inset-0 z-[110] flex items-center justify-center bg-black/40 p-4">
      <div className="bg-background-50 rounded-xl w-full max-w-lg shadow-2xl">
        <div className="flex items-center justify-between px-5 py-4 border-b border-background-200">
          <div>
            <h3 className="text-sm font-semibold text-foreground-900">下载计划表模板</h3>
            <p className="text-xs text-foreground-500 mt-0.5">
              选择下载参数，按所选周期统计已完成订单数量并预填计划数量
            </p>
          </div>
          <button
            type="button"
            disabled={downloading}
            onClick={onCancel}
            className="w-8 h-8 flex items-center justify-center rounded-lg text-foreground-400 hover:text-foreground-700 hover:bg-background-100 disabled:opacity-50"
          >
            <i className="ri-close-line text-lg" />
          </button>
        </div>

        <div className="px-5 py-4 space-y-4">
          {showDept && (
            <div>
              <label
                className={`text-sm font-medium mb-1.5 block ${
                  deptError ? 'text-red-600' : 'text-foreground-800'
                }`}
              >
                <span className="text-red-500 mr-0.5">*</span>
                采购科室
              </label>
              <SearchableSelect
                options={deptOptions}
                value={deptTitle}
                onChange={(v) => {
                  setDeptTitle(typeof v === 'string' ? v : v[0] || '');
                  setDeptError('');
                }}
                placeholder="请选择科室"
                icon="ri-hospital-line"
                searchPlaceholder="搜索科室..."
                invalid={!!deptError}
                clearable={false}
              />
              {deptError && (
                <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1 font-medium">
                  <i className="ri-error-warning-line" />
                  {deptError}
                </p>
              )}
            </div>
          )}

          <div>
            <label
              className={`text-sm font-medium mb-1.5 block ${
                dateError ? 'text-red-600' : 'text-foreground-800'
              }`}
            >
              下单数量统计周期
            </label>
            <DateRangePicker
              dateFrom={dateFrom}
              dateTo={dateTo}
              onDateFromChange={(v) => {
                setDateFrom(v);
                setDateError('');
              }}
              onDateToChange={(v) => {
                setDateTo(v);
                setDateError('');
              }}
              placeholder="不选则默认上个月"
            />
            {dateError ? (
              <p className="mt-1.5 text-xs text-red-600 flex items-center gap-1 font-medium">
                <i className="ri-error-warning-line" />
                {dateError}
              </p>
            ) : (
              <p className="mt-1.5 text-xs text-foreground-400">
                不选日期时按上个月统计；所选区间将用于预填模板中的计划数量
              </p>
            )}
          </div>
        </div>

        <div className="px-5 py-4 border-t border-background-200 flex items-center justify-end gap-2">
          <button
            type="button"
            disabled={downloading}
            onClick={onCancel}
            className="px-4 py-2 rounded-lg border border-background-300 text-sm text-foreground-600 hover:bg-background-100 disabled:opacity-50"
          >
            取消
          </button>
          <button
            type="button"
            disabled={downloading}
            onClick={handleConfirm}
            className="px-4 py-2 rounded-lg bg-blue-500 hover:bg-blue-600 text-white text-sm font-medium inline-flex items-center gap-1.5 disabled:opacity-60"
          >
            {downloading ? (
              <i className="ri-loader-4-line animate-spin" />
            ) : (
              <i className="ri-download-2-line" />
            )}
            {downloading ? '生成中...' : '下载模板'}
          </button>
        </div>
      </div>
    </div>
  );
}
